// File: src/pages/BallooningPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Splitter, Select, Typography, Layout, Row, Flex, Col, Button, Radio, Divider, Tag, Modal, Space } from 'antd';
import { ArrowLeftOutlined, CaretLeftOutlined, CaretRightOutlined, CheckOutlined } from '@ant-design/icons';
import BallooningViewer from '../../components/BallooningViewer';
import { useGetBallooningProjectQuery } from '../../redux/api/ballooning';
import InspectionResultsInputTable from '../../components/Operations/Quality/InspectionResultsInputTable';
import { useNavigate } from 'react-router-dom';
import { Balloon } from '../../interfaces/Ballooning';
import { useGetGageTypesQuery } from '../../redux/api/quality';

interface RadioOption {
	value: string;
	label: string;
}

const InspectionResultsEntryPage: React.FC = () => {
	const navigate = useNavigate();
	const { Header, Content } = Layout;

	const { Text } = Typography;
	const { id: projectId } = useParams();
	const { data: projectData, refetch } = useGetBallooningProjectQuery({
		id: projectId!,
	});
	const { data: gageTypesData } = useGetGageTypesQuery({});
	
	const headerRef = useRef(null);
	const scrollablePanelRef = useRef(null);

	const userLayoutPreference = localStorage.getItem(
		'inspectionResultsEntryLayout'
	);

	const userDefaultViewModePreference = localStorage.getItem(
		'inspectionResultsEntryDefaultView'
	);
	const [layout, setLayout] = useState<'vertical' | 'horizontal'>(
		userLayoutPreference &&
			(userLayoutPreference === 'vertical' ||
				userLayoutPreference === 'horizontal')
			? userLayoutPreference
			: 'vertical'
	);

	const [isOperatorView, setIsOperatorView] = useState(true);
	const [isReadyToComplete, setIsReadyToComplete] = useState(false);
	// Sample and inspection 
	const [currentSample, setCurrentSample] = useState<number>(1);
	const [isVisualInspectionDone, setVisualInspectionDone] = useState<string>("Not Inspected");
	const [sampleStatus, setSampleStatus] = useState('In Progress');
	const [selectedKeyenceView, setSelectedKeyenceView] = useState("None");
	const [selectedHandGage, setSelectedHandGage] = useState('all')
	const [keyenceViewOptions, setKeyenceViewOptions] = useState<RadioOption[]>([{ value: 'none', label: 'None' }]);
	const [handGageOptions, setHandGageOptions] = useState<RadioOption[]>([{ value: 'all', label: 'All' }]);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [handGageButtonText, setHandGageButtonText] = useState("All");

	const renderOptions = (options: RadioOption[], color?: string) => {

		const updatedOptions = [...options, { value: 'all', label: 'All' }];
		return updatedOptions.map((opt) => (
			<Radio.Button onClick={() => setIsModalVisible(false)} style={{ color, borderColor: color }} key={opt.value} value={opt.value}>
				{opt.label}
			</Radio.Button>
		))
	};

	const renderKeyenceStatuses = (options) => {
		const color = "blue";

		return options.map((opt) => (

			<Tag style={{ color, borderColor: color }} key={opt.value}>
				{opt.label}
			</Tag>
		))
	};


	// Local state for balloons
	const [activeBalloonId, setActiveBalloonId] = useState<string | null>(null);
	const [centerOnActive, setCenterOnActive] = useState(false);
	const [localBalloons, setLocalBalloons] = useState([]);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [pageID, setPageID] = useState<string | null>(null);
	const [balloonSize, setBalloonSize] = useState(50);

	const headerStyle: React.CSSProperties = {
		textAlign: 'center',
		verticalAlign: 'center',
		color: '#000',
		height: 'auto',
		left: 0,
		top: 0,
		lineHeight: "20px", // Ensures vertical centering
		paddingInline: 8,
		width: '100vw',
		position: 'fixed',
		zIndex: 1000,
		backgroundColor: '#f0f7f6',

	};

	const contentStyle: React.CSSProperties = {
		textAlign: 'center',
		minHeight: `calc(100vh - 70px)`, // Adjust for header height
		height: 'max-content',
		lineHeight: '110px',
		color: '#fff',
		backgroundColor: '#fff',
		marginTop: 110,
		width: '100vw',
		overflowY: 'hidden',
	};


	const layoutStyle = {
		borderRadius: 8,
		overflow: 'hidden',
		color: '#000',
		//width: 'calc(100% - 2px)',
		//maxWidth: 'calc(100% - 2px)',
	};

	const radioGroupStyle: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	  };

	const getUniqueValues = (data: [], key: string) => {
		return data.reduce(
			(acc, item) => {
				const value = item[key];

				if (value != null && !acc.uniqueValues.has(value)) {
					acc.uniqueValues.add(value);
					acc.result.push({ value, label: value });
				}

				return acc;
			},
			{ uniqueValues: new Set(), result: [] }
		).result;
	};

	const filteredBalloons = (ballooningProject) => {
		let balloons = ballooningProject.ballooning_pages.flatMap(page => page.balloons || []);
		balloons = balloons.filter((b) => b.measurement_strategy !== "Keyence");

		if (selectedHandGage !== "all") {
			balloons = balloons.filter(b => b.gage_type === selectedHandGage);
		}


		return balloons;
	};

	const mapFieldsToOptions = (ids, idNameList) => {
		return ids.map(item => {
			const match = idNameList.find(entry => entry.id === item.value);
			return {
				...item,
				label: match ? match.name : `Unknown (${item.value})`
			};
		});
	};


	useEffect(() => {
		if (projectData) {
			setBalloonSize(projectData.balloon_size);
		}

		if (projectData && projectData.ballooning_pages) {
			//const allBalloons =  projectData.ballooning_pages.flatMap(page => page.balloons || []);

			setLocalBalloons(filteredBalloons(projectData));

			const selectedPage = projectData.ballooning_pages.find(
				page => page.page_number === pageNumber
			);


			if (selectedPage) {
				setPageID(selectedPage.id);
				setKeyenceViewOptions(getUniqueValues(selectedPage.balloons, 'view'));
				const gageTypes = getUniqueValues(selectedPage.balloons, 'gage_type');
				const mappings = mapFieldsToOptions(gageTypes, gageTypesData);

				setHandGageOptions(mappings);
				selectedHandGage === 'all' ? setHandGageButtonText("All")
					: 
					setHandGageButtonText(mappings.find(g => g.value === selectedHandGage)?.label);
			
			}
			if (sampleStatus === "In Progress" && true) 
				setIsReadyToComplete(true)
		}
	}, [projectData, pageNumber, selectedHandGage]);

	

	useEffect(() => {
		if (gageTypesData) {
			//setHandGageOptions(mapFieldsToOptions(gageTypes, gageTypesData));
		}
	}, [gageTypesData])


	const renderHandGageOptions = () => (
		<div style={{ display: "flex", flexWrap: "wrap", gap: "16px", justifyItems: 'center' }}>
		<Radio.Group
			defaultValue={selectedHandGage}
			buttonStyle="solid"
			size="large"
			style={radioGroupStyle}
			onChange={(e) => setSelectedHandGage(e.target.value)} value={selectedHandGage} >
				{renderOptions(handGageOptions, "")}
		
		</Radio.Group>
		</div>
	);

	return (
		<div style={{overflow: "hidden"}}> 
			<Flex gap="middle">
				<Layout style={layoutStyle}>
					<Header style={headerStyle}>
						<Row justify="space-between" align='middle' style={{ height: "100%" }}>
							<Col>
								<Row>
									Total Qty: <Tag color="blue">{projectData?.order_line_item.quantity}</Tag>
									Sample Size: <Tag > {projectData?.order_line_item.quantity}</Tag>
								</Row>
								<Row>
									Interval: <Tag>{projectData?.order_line_item.quantity} </Tag>
									in <Tag>{projectData?.order_line_item.quantity} </Tag> Parts
								</Row>
							</Col>
							<Col>
								<Row>
									<Tag color="green">{projectData?.order_line_item.quantity} </Tag> Pass
									<Tag color="red">{projectData?.order_line_item.quantity} </Tag> Failed
								</Row>

								<Row>
									<Tag color="yellow">{projectData?.order_line_item.quantity} </Tag>In Process
									<Tag >{projectData?.order_line_item.quantity} </Tag>Not Started
								</Row>
							</Col>
							<Col >
								<Row>
									<Button
										type="text"
										style={{ fontSize: '20px', color: "#000	" }}
										onClick={() => {
											navigate('/tablet/operations/qc/machine');
										}}
									>
										<ArrowLeftOutlined />
										<span>Back</span>
									</Button>
								</Row>

							</Col>
						</Row>
						<Row justify="space-between" align='middle' >
							<Col>
								
								{/* <Row justify="space-between" align='middle'>

									{ !isOperatorView ?
										<div>
											<Text strong> Keyence:</Text>
											<Radio.Group buttonStyle="solid" onChange={(e) => setSelectedKeyenceView(e.target.value)} value={selectedKeyenceView}>
												{renderOptions(keyenceViewOptions, "")}
											</Radio.Group>
										</div>
										: <div style={{ alignContent: 'left' }} >
											<Text strong> Keyence:</Text>
											{renderKeyenceStatuses(keyenceViewOptions)}
										</div>

									}
								</Row> */}
								<Row gutter={[20,20]} style={{ padding: 10 ,height: "100%" }}>
									<Col>
										<Text strong> Hand: </Text>
										<Button 
										size="large"
										style={{
											backgroundColor: handGageButtonText === "All" ? "white" : "white",
											borderColor: handGageButtonText === "All" ? "red" : "red",
											color: "red",
										}} 
										onClick={() => setIsModalVisible(true)}>

											 {handGageButtonText}
										</Button>
									</Col>
									
									<Col>
									<Text strong> Visual Inspection: </Text>
										<Button
											type="primary"
											
											
											size="large"
											style={{
												backgroundColor: isVisualInspectionDone === "Passed" ? "green" : "gray",
												borderColor: isVisualInspectionDone === "Passed" ? "green" : "gray",
												color: "white",
											}}

											onClick={() => setVisualInspectionDone("Passed")}
											disabled={isVisualInspectionDone === "Passed"}
										>
											{isVisualInspectionDone}

										</Button>
									</Col>
									<Col>
										<Button
											type="primary"
											icon={ sampleStatus === "Completed" && <CheckOutlined /> }
											size="large"
											disabled={!isReadyToComplete || sampleStatus === "Completed"}
											onClick={() => setSampleStatus("Completed")}
											style={{
												backgroundColor: sampleStatus === "Completed" ? "green" : "gray",
												borderColor: sampleStatus === "Completed" ? "green" : "gray",
												color: "white",
												
											}}
										>
											{ sampleStatus }
										</Button>
									</Col>
								</Row>
							</Col>

							<Col>
								<Button
									type="text"
									style={{ fontSize: '30px', color: "#000	" }}
									onClick={() => {
										navigate('/tablet/operations/qc/machine');
									}}
								>
									<CaretLeftOutlined />

								</Button>
								<Button
									type="text"
									style={{ fontSize: '30px', color: "#000	" }}
									onClick={() => {
										navigate(`/tablet/operations/qc/samples/${projectData.id}`);
									}}
								>

									<span>1</span>
								</Button>
								<Button
									type="text"
									style={{ fontSize: '30px', color: "#000	" }}
									onClick={() => {
										navigate('/tablet/operations/qc/machine');
									}}
								>
									<CaretRightOutlined />

								</Button>
							</Col>
						</Row>

					</Header>
					<Content style={contentStyle}>
						<div ref={headerRef} className="flex flex-col justify-between">
							
						</div>
						<div
							style={{
								height: `calc(100vh - ${headerRef?.current?.clientHeight}px - 1rem)`,
							}}
						>

							<Splitter className="gap-1 w-full" layout={layout} key={layout}>
								<Splitter.Panel className="flex flex-col gap-1 h-full" style={{ zIndex: '20' }} resizable={false} min={100}>
									<BallooningViewer
										activeBalloonId={activeBalloonId}
										setActiveBalloonId={setActiveBalloonId}
										setCenterOnActive={setCenterOnActive}
										centerOnActive={centerOnActive}
										localBalloons={localBalloons}
										setLocalBalloons={setLocalBalloons}
										balloonSize={balloonSize}
										editable={false}
										pageID={pageID}
										pageNumber={pageNumber}
										setPageNumber={setPageNumber}
										isTabletView={true}
										showLeaderLines
									/>
								</Splitter.Panel>
								<Splitter.Panel
									className="border bg-white gap-1 h-full" 
									resizable={false} 
									style={{ 
										/* overscrollBehavior: "contain",
										WebkitOverflowScrolling: "touch",
										touchAction: "pan-y", 
										 */flex:'1', 
										overflowY: 'auto',
										padding: '40px',
										marginBottom: '20px',
										overflowX: "hidden" 
									}}>
									<InspectionResultsInputTable
										balloons={localBalloons}
										pageNumber={pageNumber}
										selectedView={selectedKeyenceView}
										selectedGage={selectedHandGage}
										isOperatorView={isOperatorView} />
										
								</Splitter.Panel>
							</Splitter>
						</div>

					</Content>
				</Layout>
			</Flex>
			<Modal
				title="Gage Types"
				open={isModalVisible}
				onOk={() => setIsModalVisible(false)}
				onCancel={() => setIsModalVisible(false)}
				onClose={() => setIsModalVisible(false)}
				width='380px'		
			>	
				{renderHandGageOptions()}
			</Modal>
		</div>
	);
};

export default InspectionResultsEntryPage;
